import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, useTheme, Button, MenuItem } from '@mui/material';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { tokens } from '../../theme';
import { TitleScreen, SubTitleScreen, PrimaryButton } from '../../SystemDesign/Styles';
import MuiDialog from '../../SystemDesign/MuiDialog';
import AddIcon from '@mui/icons-material/Add';
import CreateQuiz from './CreateQuiz';
import SearchInput from '../../SystemDesign/SearchInput';
import FilterSelector from '../../SystemDesign/FilterSelector';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import CardMenuActions from '../../SystemDesign/CardMenuActions';
import PaginationUI from '../../SystemDesign/PaginationUI';
import QuizCard from '../../SystemDesign/QuizCard';
import UpdateQuiz from './UpdateQuiz';
import DeleteQuiz from './DeleteQuiz';
import { useGetQuizzesQuery } from '../../services/SkillsManagement';
import { CLEAR_QUIZZES_LIST, INITIALIZE_QUIZZES } from '../../Reducers/Actions';
import QuestionList from '../QuizQuestions/QuestionList';
import ViewToggle from '../../SystemDesign/ViewToggle';
import QuizAssignementList from '../Assignement/QuizAssignementList';
import CreateAssignement from '../Assignement/CreateAssignement';

const QuizList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [assignmentsSearch, setAssignmentsSearch] = useState('');
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [questionsDlg, setQuestionsDlg] = useState(() => {
    try {
      const raw = localStorage.getItem('questionsDlg');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && parsed.open ? parsed : { open: false, quizId: null, title: '', posteId: null };
    } catch (_) {
      return { open: false, quizId: null, title: '', posteId: null };
    }
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('quizzes-rowsPerPage');
    return saved !== null ? parseInt(saved, 10) : 10;
  });
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'true' | 'false'
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('quizzes-viewMode') || 'cards'); // 'cards' => Quizzes, 'table' => Assignments

  const { data, error, isLoading, isFetching } = useGetQuizzesQuery(
    {
      page,
      limit: rowsPerPage,
      search: searchTerm || undefined,
      isActive: activeFilter === 'all' ? undefined : activeFilter === 'true',
    },
    { refetchOnMountOrArgChange: true }
  );
  const apiQuizzes = data?.quizzes ?? data?.data?.quizzes;
  const apiPagination = data?.pagination ?? data?.data?.pagination;
  const quizzes = useSelector((state) => state.quizzes || []);
  const renderList = (quizzes && quizzes.length > 0) ? quizzes : (apiQuizzes || []);

  // Sync API data into Redux reducer (similar to PostesList)
  useEffect(() => {
    if (Array.isArray(apiQuizzes)) {
      dispatch({ type: CLEAR_QUIZZES_LIST });
      dispatch({ type: INITIALIZE_QUIZZES, payload: apiQuizzes });
    }
  }, [apiQuizzes, dispatch]);

  // Handle search
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (!value) setPage(1);
  };

  const handleSearchSubmit = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const applyFilters = (paginationInfo = null, _sortingInfo = null, searchValue = null) => {
    if (searchValue !== null) setSearchTerm(searchValue);
    if (paginationInfo?.rowsPerPage) {
      setRowsPerPage(paginationInfo.rowsPerPage);
      localStorage.setItem('quizzes-rowsPerPage', String(paginationInfo.rowsPerPage));
    }
    setPage(paginationInfo?.currentPage || 1);
  };

  // Keep localStorage in sync with dialog state
  useEffect(() => {
    try {
      if (questionsDlg?.open) {
        localStorage.setItem('questionsDlg', JSON.stringify(questionsDlg));
      } else {
        localStorage.removeItem('questionsDlg');
      }
    } catch (_) { /* ignore */ }
  }, [questionsDlg]);

  // Debug: log dialog open state changes and loading transitions (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[QuizList] questionsDlg=', questionsDlg);
    }
  }, [questionsDlg]);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[QuizList] loading flags changed', { isLoading, isFetching });
    }
  }, [isLoading, isFetching]);

  return (
    <Box p={3}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <div>
            <Typography sx={TitleScreen(colors)}>Quizzes</Typography>
            <Typography sx={SubTitleScreen(colors)}>
              Manage quizzes and questions.
            </Typography>
          </div>
          <div>
            {viewMode === 'cards' ? (
              <Button
                variant="contained"
            color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsOpen(true)}
              >
                Add Quiz
              </Button>
            ) : (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setIsAssignOpen(true)}
                variant="contained"
                color="primary"              >
                Add Assignment
              </Button>
            )}
          </div>
        </div>

        {/* Search + Filters + View Toggle + (Add Assignment in assignments view) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1.5rem' }}>
          {viewMode === 'cards' && (
            <>
              <div style={{ flexGrow: 1 }}>
                <SearchInput
                  placeholder="Search quizzes..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSearch={handleSearchSubmit}
                />
              </div>
              <div style={{ width: 220 }}>
                <FilterSelector
                  value={activeFilter}
                  onChange={(val) => { setActiveFilter(val); setPage(1); }}
                  options={[
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' },
                  ]}
                  defaultLabel="All statuses"
                  width="220px"
                />
              </div>
            </>
          )}
          {viewMode === 'table' && (
            <div style={{ flexGrow: 1 }}>
            
            </div>
          )}
          <ViewToggle
            value={viewMode}
            onChange={(mode) => { setViewMode(mode); localStorage.setItem('quizzes-viewMode', mode); }}
            cardsLabel="Quizzes"
            tableLabel="Assignments"
            label="View:"
          />
        </div>

        <MuiDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          icon={<AddIcon />}
          title="Create New Quiz"
          subtitle="Create a new quiz question."
          content={<CreateQuiz open={isOpen} onClose={() => setIsOpen(false)} />}
          maxWidth="md"
          fullWidth
        />

        {/* Create Assignment Dialog */}
        <MuiDialog
          open={isAssignOpen}
          onClose={() => setIsAssignOpen(false)}
          icon={<AddIcon />}
          title="Create New Assignment"
          subtitle="Assign a quiz to a user."
          content={
            <CreateAssignement
              open={isAssignOpen}
              onClose={() => setIsAssignOpen(false)}
              onSubmit={() => { setViewMode('table'); localStorage.setItem('quizzes-viewMode', 'table'); }}
            />
          }
          maxWidth="sm"
          fullWidth
        />

        {isLoading ? (
          <LoaderSpinner loading={isLoading || isFetching} />
        ) : viewMode === 'cards' ? (
          <>
            {/* Quizzes grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
              {renderList.map((quiz, index) => (
                <div key={quiz.quizId || quiz.id || index} style={{ position: 'relative' }}>
                  <QuizCard
                    quiz={quiz}
                    index={index}
                    onView={undefined}
                    onEdit={() => {}}
                    onDelete={() => {}}
                  />
                  <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                    <CardMenuActions>
                      {/* Normalize quizId for action components to avoid NaN ids */}
                      {
                        (() => {
                          const normalizedId = Number.isFinite(Number(quiz?.quizId))
                            ? Number(quiz.quizId)
                            : (Number.isFinite(Number(quiz?.id)) ? Number(quiz.id) : undefined);
                          const normalized = normalizedId != null ? { ...quiz, quizId: normalizedId } : quiz;
                          return (
                            <>
                              <UpdateQuiz row={{ original: normalized }} actionKey="update" />
                              <DeleteQuiz row={{ original: normalized }} actionKey="delete" />
                            </>
                          );
                        })()
                      }
                      <MenuItem onClick={() => {
                        const qid = Number.isFinite(Number(quiz?.quizId)) ? Number(quiz.quizId) : Number(quiz?.id);
                        setQuestionsDlg({ open: true, quizId: qid, title: quiz.title, posteId: quiz.posteId || quiz.poste_id || null });
                      }}>
                        <ListAltOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                        Manage Questions
                      </MenuItem>
                    </CardMenuActions>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <PaginationUI
              paginationInfo={{
                currentPage: page,
                rowsPerPage,
                total: apiPagination?.total ?? apiPagination?.totalItems ?? apiPagination?.totalElements ?? 0,
              }}
              isLoading={isLoading || isFetching}
              onPageChange={(p, rpp) => applyFilters({ currentPage: p, rowsPerPage: rpp })}
              onRowsPerPageChange={(rpp) => applyFilters({ currentPage: 1, rowsPerPage: rpp })}
            />
          </>
        ) : (
          <QuizAssignementList showHeader={false} showAddButton={false} showSearch={false} />
        )}

        {/* Full-screen Questions Manager: keep mounted regardless of loading/view state */}
        <QuestionList
          key={questionsDlg.quizId || 'no-quiz'}
          open={questionsDlg.open}
          onClose={(_e, reason) => {
            if (reason === 'closeButton') {
              setQuestionsDlg({ open: false, quizId: null, title: '', posteId: null });
            } else {
              // ignore backdrop/escape or other unintended reasons
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.debug('[QuizList] Ignored onClose from QuestionList with reason=', reason);
              }
            }
          }}
          quizId={questionsDlg.quizId}
          quizTitle={questionsDlg.title}
          posteId={questionsDlg.posteId}
        />
      </div>
    </Box>
  );
};

export default QuizList;

